import React, { useCallback, useState } from 'react';
import { Box, Stack } from '@mui/material';

import useComponentMounted from '../../../../hooks/useComponentMounted';
import useToast from '../../../hooks/useToast';

import MassTextCampaign from '../../../../Model/MassTextCampaign';
import { useMassTextCompose } from '../hooks/useMassTextCompose';
import { useRecipientCount } from '../hooks/useRecipientCount';
import { formatAudience } from '../utils/formatAudience';
import { hasFirstNameToken } from '../utils/composeBodyPreview';
import { reportError } from '../utils/reportError';
import { sendToast } from '../utils/sendToast';
import { uploadCampaignImage } from '../utils/uploadCampaignImage';
import { submitErrorMessage } from '../utils/errorMessages';
import {
  clearBrevoAuthSession,
  getBrevoAuthSession,
  isSessionActive,
  saveBrevoAuthSession,
} from '../utils/brevoAuth';
import { SUBMIT_PHASE } from '../utils/constants';

import texts from '../texts.json';
import { GridLayout } from '../styles';

import CoachSection from '../components/CoachSection';
import DplSection from '../components/DplSection';
import CampaignNameSection from './sections/CampaignNameSection';
import AudienceSection from './sections/AudienceSection';
import ImageSection from './sections/ImageSection';
import MessageSection from './sections/MessageSection';
import SidePanel from './SidePanel/SidePanel';
import ConfirmSendDialog from './ConfirmSendDialog';
import BrevoSessionBanner from './components/BrevoSessionBanner';
import BrevoSessionDialog from './components/BrevoSessionDialog';

const ComposeTab = () => {
  const {
    name, setName,
    coach, setCoach,
    audience, setAudience, toggleCountry, toggleSalesStage, setAudienceType, setRequireConsent,
    dpl, setDpl,
    body, setBody,
    image, setImage,
    coachResetKey,
    derived,
    reset,
  } = useMassTextCompose();

  const { showToast } = useToast();
  const isMountedRef = useComponentMounted();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [submitPhase, setSubmitPhase] = useState(SUBMIT_PHASE.IDLE);
  const [imageHasError, setImageHasError] = useState(false);
  const isSubmitting = submitPhase !== SUBMIT_PHASE.IDLE;

  const [brevoSession, setBrevoSession] = useState(() => getBrevoAuthSession());
  const [isBrevoDialogOpen, setIsBrevoDialogOpen] = useState(false);
  const isBrevoConfigured = isSessionActive(brevoSession);

  const handleOpenBrevoDialog = useCallback(() => {
    setIsBrevoDialogOpen(true);
  }, []);

  const handleCloseBrevoDialog = useCallback(() => {
    setIsBrevoDialogOpen(false);
  }, []);

  const handleSaveBrevoSession = useCallback((session) => {
    const persisted = saveBrevoAuthSession(session);
    if (!persisted) return;
    setBrevoSession(persisted);
    setIsBrevoDialogOpen(false);
    showToast(texts.brevoSession.dialog.saved);
  }, [showToast]);

  const handleClearBrevoSession = useCallback(() => {
    clearBrevoAuthSession();
    setBrevoSession(null);
    setIsBrevoDialogOpen(false);
    showToast(texts.brevoSession.dialog.cleared);
  }, [showToast]);

  const coachId = coach ? coach.id : null;
  const { coachName } = derived;
  const preview = useRecipientCount(coachId, audience, hasFirstNameToken(body));
  const audienceLabel = formatAudience(audience);

  const isValid = derived.audienceComplete
    && derived.dplComplete
    && derived.messageComplete
    && !preview.loading
    && !preview.error
    && preview.count > 0
    && !imageHasError;

  const handleSendClick = useCallback(() => {
    if (!isValid) return;
    setIsConfirmOpen(true);
  }, [isValid]);

  const handleConfirm = useCallback(async () => {
    if (!isValid || isSubmitting) return;
    const willUpload = !!image?.file;
    setSubmitPhase(willUpload ? SUBMIT_PHASE.UPLOADING : SUBMIT_PHASE.SENDING);
    try {
      let media = null;
      if (willUpload) {
        media = await uploadCampaignImage({ file: image.file });
        if (!isMountedRef.current) return;
        setSubmitPhase(SUBMIT_PHASE.SENDING);
      }
      const result = await MassTextCampaign.send({
        name: derived.effectiveName,
        coachId,
        body,
        audience,
        dpl,
        media,
      });
      if (!isMountedRef.current) return;
      setIsConfirmOpen(false);
      setSubmitPhase(SUBMIT_PHASE.IDLE);
      reset();
      // The send is built in the request when it can be, and left for the hourly job when Brevo is
      // rate limiting or out of list headroom. Say which happened rather than promising either.
      showToast(sendToast(result?.outcome));
    } catch (err) {
      reportError('sendCampaign', err);
      if (!isMountedRef.current) return;
      setIsConfirmOpen(false);
      setSubmitPhase(SUBMIT_PHASE.IDLE);
      showToast(submitErrorMessage(err, texts.sendStatus.error), { error: true });
    }
  }, [
    isValid, derived.effectiveName, isSubmitting,
    body, coachId, audience, dpl, image,
    showToast, reset, isMountedRef,
  ]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <BrevoSessionBanner
          isConfigured={isBrevoConfigured}
          expiresAt={brevoSession ? brevoSession.expiresAt : null}
          onConfigureClick={handleOpenBrevoDialog}
        />
      </Box>
      <GridLayout>
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <CampaignNameSection
            value={name}
            autoGeneratedName={derived.autoGeneratedName}
            onChange={setName}
          />
          <CoachSection resetKey={coachResetKey} onChange={setCoach} />
          <AudienceSection
            audience={audience}
            onAudienceChange={setAudience}
            onAudienceTypeChange={setAudienceType}
            onToggleSalesStage={toggleSalesStage}
            onToggleCountry={toggleCountry}
            onRequireConsentChange={setRequireConsent}
          />
          <DplSection
            coachId={coachId}
            dpl={dpl}
            onChange={setDpl}
          />
          <ImageSection image={image} onChange={setImage} onError={setImageHasError} />
          <MessageSection
            body={body}
            onBodyChange={setBody}
            coachName={coachName}
            hasImage={!!image}
          />
        </Stack>
        <Stack spacing={2} sx={{ position: 'sticky', top: 24, minWidth: 0 }}>
          <SidePanel
            preview={preview}
            coachName={coachName}
            body={body}
            imageUrl={image?.previewUrl || ''}
            isReady={isValid}
            isSubmitting={isSubmitting}
            onSendClick={handleSendClick}
          />
        </Stack>
      </GridLayout>

      <ConfirmSendDialog
        isOpen={isConfirmOpen}
        isSubmitting={isSubmitting}
        loadingMessage={submitPhase === SUBMIT_PHASE.UPLOADING
          ? texts.confirmModal.uploadingImage
          : texts.confirmModal.creatingCampaign}
        coachName={coachName}
        audienceLabel={audienceLabel}
        showConsentWarning={derived.isClientAudience}
        recipientCount={preview.count}
        isCapped={!!preview.capped}
        dplName={dpl ? dpl.name : ''}
        imageUrl={image?.previewUrl || ''}
        body={body}
        onConfirm={handleConfirm}
        onCancel={() => setIsConfirmOpen(false)}
      />

      <BrevoSessionDialog
        isOpen={isBrevoDialogOpen}
        initialSession={brevoSession}
        onSave={handleSaveBrevoSession}
        onClear={handleClearBrevoSession}
        onClose={handleCloseBrevoDialog}
      />
    </Box>
  );
};

export default ComposeTab;
